import { useEffect, useState } from 'react';
import { getMyApplication, TeacherApplication } from '../api/teacher-applications';
import { ApplicationStateOrMissing } from '../components/ApplicationStatusBanner';

interface TeacherApplicationView {
  loading: boolean;
  application: TeacherApplication | null;
  /** 'MISSING' when the teacher has never started one. */
  state: ApplicationStateOrMissing;
  reason?: string;
}

/**
 * Loads the signed-in teacher's application.
 *
 * A 404 is a normal state, not an error — it means the teacher has not started
 * an application yet, which is exactly the case the banner needs to prompt. Any
 * other failure also resolves to 'MISSING' rather than throwing, because this
 * is supporting context on a screen: it should never blank out a dashboard that
 * is otherwise fine.
 */
export function useTeacherApplication(enabled = true): TeacherApplicationView {
  const [loading, setLoading] = useState(enabled);
  const [application, setApplication] = useState<TeacherApplication | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const detail = await getMyApplication();
        if (!cancelled) setApplication(detail.application ?? null);
      } catch {
        if (!cancelled) setApplication(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return {
    loading,
    application,
    state: application?.state ?? 'MISSING',
    reason: application?.reviewReason,
  };
}
